// اختيار الإدارة بالبحث الذكي — تطبيع عربي + Fuzzy Matching + مستويات ثقة
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useDebounced } from "@/v2/lib/hooks";
import { Search, CheckCircle2, HelpCircle, ListFilter, Building2, X } from "lucide-react";

export default function DeptPicker({ value, typed, onChange, testId = "dept-picker" }) {
  const [q, setQ] = useState(typed || "");
  const [open, setOpen] = useState(false);
  const dq = useDebounced(q, 350);

  const { data } = useQuery({
    queryKey: ["dept-search", dq],
    queryFn: async () => (await api.get(`/departments/search?q=${encodeURIComponent(dq)}`)).data,
    enabled: open || dq.length > 1,
    staleTime: 60000,
  });

  const auto = data?.confidence === "high" ? data.department : null;
  useEffect(() => {
    if (auto && auto.id !== value?.id) onChange(auto, q);
  }, [auto?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (d) => { onChange(d, q); setOpen(false); };
  const suggestions = data?.suggestions || [];
  const conf = q.trim() ? data?.confidence : null;

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="relative">
        <Search size={18} className="absolute top-1/2 -translate-y-1/2 right-4 text-[#bcc8cf] pointer-events-none" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onChange(null, e.target.value); }}
          onFocus={() => setOpen(true)}
          data-testid={`${testId}-input`}
          placeholder="اكتب اسم إدارتك... (لا تقلق من الأخطاء الإملائية)"
          className="v2-input !h-14 text-base" style={{ paddingInlineStart: "3rem" }} />
        {q && (
          <button type="button" onClick={() => { setQ(""); onChange(null, ""); }} data-testid={`${testId}-clear`}
            className="absolute top-1/2 -translate-y-1/2 left-4 text-[#bcc8cf] hover:text-white"><X size={16} /></button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {value ? (
          <motion.div key="picked" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            data-testid={`${testId}-resolved`}
            className="flex items-center gap-3 p-4 rounded-2xl border border-[#91cab8]/40 bg-[#91cab8]/10">
            <CheckCircle2 className="text-[#91cab8] shrink-0" size={22} />
            <div className="min-w-0">
              <div className="text-xs text-[#bcc8cf]">تم التعرف على إدارتك</div>
              <div className="font-extrabold truncate">{value.name}</div>
            </div>
            <span className="ms-auto text-[10px] font-bold text-[#bcc8cf]">{value.code}</span>
          </motion.div>
        ) : conf === "medium" ? (
          <motion.div key="med" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-2" data-testid={`${testId}-didyoumean`}>
            <div className="flex items-center gap-2 text-sm text-[#e3c799] font-bold"><HelpCircle size={16} /> هل تقصد إحدى هذه الإدارات؟</div>
            {(data?.candidates || []).slice(0, 3).map((c) => (
              <button key={c.id} type="button" onClick={() => pick(c)} data-testid={`${testId}-cand-${c.id}`}
                className="w-full flex items-center gap-3 p-3.5 rounded-2xl v2-inset hover:border-[#bb9661]/50 transition-colors text-start">
                <Building2 size={18} className="text-[#e3c799] shrink-0" />
                <span className="font-bold flex-1 truncate">{c.name}</span>
                <span className="text-[10px] text-[#bcc8cf]">{c.code}</span>
              </button>
            ))}
          </motion.div>
        ) : conf === "low" ? (
          <motion.div key="low" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="space-y-2" data-testid={`${testId}-list`}>
            <div className="flex items-center gap-2 text-sm text-[#bcc8cf]"><ListFilter size={16} /> لم نتمكن من تحديد الإدارة بدقة، اختر إدارتك من القائمة</div>
            <div className="max-h-56 overflow-y-auto space-y-2 pe-1">
              {suggestions.map((c) => (
                <button key={c.id} type="button" onClick={() => pick(c)} data-testid={`${testId}-opt-${c.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl v2-inset hover:border-[#bb9661]/50 text-start">
                  <Building2 size={16} className="text-[#bcc8cf] shrink-0" />
                  <span className="font-semibold flex-1 truncate text-sm">{c.name}</span>
                  <span className="text-[10px] text-[#bcc8cf]">{c.code}</span>
                </button>
              ))}
            </div>
          </motion.div>
        ) : open && !q ? (
          <motion.div key="all" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-h-56 overflow-y-auto space-y-2 pe-1" data-testid={`${testId}-all`}>
            {suggestions.map((c) => (
              <button key={c.id} type="button" onClick={() => pick(c)} data-testid={`${testId}-opt-${c.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-2xl v2-inset hover:border-[#bb9661]/50 text-start">
                <Building2 size={16} className="text-[#bcc8cf] shrink-0" />
                <span className="font-semibold flex-1 truncate text-sm">{c.name}</span>
                <span className="text-[10px] text-[#bcc8cf]">{c.code}</span>
              </button>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
